import { createContext, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

/*
 * Where a platform surface goes when it is already inside a panel.
 *
 * The surfaces a step anchors to have their own onward links: the dive page
 * offers "revise this skill", the builder finishes with "go vet it". Called
 * from a page those are routes. Called from inside a panel they have to stay
 * in the panel, or the flow the panel exists to hold breaks on the page's own
 * internal link, which is the same defect as Continue navigating one level
 * further in.
 *
 * The panel is the provider. With no provider `go` is `navigate` and `done` is
 * the route the page always used, so every one of these pages behaves exactly
 * as it did when opened on its own.
 */

export interface FeatureNav {
  open: (route: string) => void;
  close: () => void;
}

export const FeatureNavContext = createContext<FeatureNav | null>(null);

export function useFeatureNav() {
  const panel = useContext(FeatureNavContext);
  const navigate = useNavigate();

  const go = useCallback((route: string) => {
    if (panel) {
      panel.open(route);
      return;
    }
    navigate(route);
  }, [panel, navigate]);

  // The page is finished with itself. In a panel that means closing; on its
  // own it means the route the page would have gone to.
  const done = useCallback((fallback: string) => {
    if (panel) {
      panel.close();
      return;
    }
    navigate(fallback);
  }, [panel, navigate]);

  return { go, done, inPanel: !!panel };
}
